'use client'

import { formatRelative } from 'date-fns'

import type { LanguageId } from '@mntn-dev/domain-types'
import { getLocale } from '@mntn-dev/i18n'
import { themeTextColorMap } from '@mntn-dev/ui-theme'

import type { PropsWithChildren } from 'react'
import { type TestIds, getTestProps } from '../../utils/testing.ts'
import { Avatar } from '../avatar/index.ts'
import { Stack } from '../stack/index.ts'
import { Icon } from '../icon/index.ts'

type FeedItemProps = Readonly<
  TestIds &
    PropsWithChildren<{
      avatar?: JSX.Element
      timestamp: Date
      languageId: LanguageId
      indicator?: boolean
    }>
>

const FeedItem = ({
  avatar,
  timestamp,
  dataTestId,
  dataTrackingId,
  languageId,
  indicator,
  children,
}: FeedItemProps) => {
  const locale = getLocale(languageId)

  return (
    <li
      className="relative flex gap-x-4 p-8 pr-24"
      {...getTestProps({ dataTestId, dataTrackingId })}
    >
      {indicator && (
        <Icon
          name="CircleIcon"
          size="2xs"
          color="negative"
          className="absolute top-2 left-2"
        />
      )}
      <Avatar>{avatar}</Avatar>
      <Stack direction="col" width="full">
        <time
          dateTime={timestamp.toLocaleDateString()}
          className={`${themeTextColorMap.disabled} text-sm font-medium first-letter:uppercase`}
        >
          {formatRelative(timestamp, new Date(), { locale })}
        </time>
        <Stack direction="col" gap="4" width="full">
          {children}
        </Stack>
      </Stack>
    </li>
  )
}

export { FeedItem, type FeedItemProps }
